import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import type { JSX, ReactNode } from 'react';

/**
 * LE TRANSPORT D'URL de l'etat d'ecran.
 *
 * `useScreen` tient l'etat (la vue active, l'element ouvert, la section, la
 * pastille) ; ce port dit ou cet etat s'ECRIT et d'ou il se RELIT. Le shell le
 * fournit en le branchant sur son routeur ; personne d'autre.
 *
 * POURQUOI UN PORT ET PAS UN IMPORT DIRECT DU ROUTEUR. `@taki/ui` est un design
 * system : il ne depend ni de `@tanstack/react-router`, ni de la couche
 * donnees, et un ecran doit pouvoir se monter hors du shell (un banc d'essai, un test,
 * une future surface portail) sans routeur du tout. D'ou un contexte
 * NULLABLE : sans fournisseur, `useScreen` garde exactement le comportement
 * d'avant (etat en memoire plus reprise de session), et rien ne casse.
 *
 * CE QUI A LE DROIT D'ALLER DANS L'ADRESSE : des IDENTIFIANTS, jamais de la
 * donnee personnelle. Un id de vue, un id d'element, un id de section, des ids
 * de filtres. Le TEXTE DE RECHERCHE en est exclu par decision explicite du
 * fondateur (30/07/2026) : on y tape un nom ou un email, et une adresse se
 * copie dans un ticket, un mail, un historique de navigateur et les journaux
 * d'un serveur. LA PILE DE TRI non plus n'y entre pas (gabarit v2, meme
 * decision) : elle decrit une maniere de regarder, pas un endroit.
 *
 * LES CLES : `view` (id de vue), `id` (element ouvert), `s` (id de
 * porte, ou de section pour un ecran d'avant les portes : meme parametre,
 * meme sens "quel jeu de donnees"), `f` (les filtres actifs), `arch` (le
 * MODE ARCHIVES), `mod` (le MODE MODELES, son jumeau : voir
 * `readModesParams`, qui tient leur exclusivite a un seul endroit) et `dans`
 * (la porte ouverte DANS une fiche parente, `views/porteDans.ts`), plus une
 * huitieme depuis le chantier `fiche-suivi` : `apercu`, l'id du brouillon de
 * vue HTML qu'on regarde sur la fiche ouverte (un id, jamais du contenu). Depuis le
 * gabarit v2, `f` est une LISTE d'ids separes par
 * des virgules (multi-filtres additifs) : une adresse V1 a un seul id reste
 * une liste valide d'un element, aucun lien existant ne casse. Corollaire
 * assume : un id de filtre ne contient JAMAIS de virgule (verifie en
 * developpement par `joinFilterIds`).
 *
 * POURQUOI `arch` ET `mod` SONT DANS L'ADRESSE alors que la pile de tri n'y est pas
 * (chantier 2_0, decision du fondateur) : le tri decrit une maniere de
 * REGARDER, ce mode-ci decide QUELS ENREGISTREMENTS EXISTENT a l'ecran. Un
 * lien partage qui perdrait cette information ouvrirait une AUTRE liste que
 * celle qu'on a envoyee, sans que ni l'expediteur ni le destinataire ne
 * puissent s'en apercevoir.
 */

/** Les parametres d'adresse d'un ecran. Cle absente = parametre absent. */
export type ScreenSearch = Record<string, string | undefined>;

/**
 * D'OU L'ON VIENT, tel que l'ecran le sait au moment du saut : la fiche ou l'on
 * ARRIVE (`porte`, `id`) et celle qu'on QUITTE (`titre`, `adresse`). C'est la
 * moitie que l'ecran remplit ; le port complete le reste (`OrigineDEtape`).
 */
export interface OrigineDeFiche {
  /** La porte de la fiche ou l'on ARRIVE : la puce ne parle que sur elle. */
  porte: string;
  /** La fiche ou l'on ARRIVE (`BROUILLON_ID` pour une fiche vide) : la puce ne parle que sur elle. */
  id: string;
  /** Le titre de la fiche d'ou l'on VIENT : ce que la puce ECRIT. Jamais vide. */
  titre: string;
  /** L'adresse d'ecran de la fiche d'ou l'on vient : ce que la puce REJOUE quand elle ne peut pas depiler. */
  adresse: ScreenSearch;
}

/**
 * L'ORIGINE D'UNE FICHE, RANGEE DANS L'ETAT DE SON ETAPE D'HISTORIQUE
 * (`history.state`), jamais dans l'adresse : la puce « Revenir a ... » de la
 * fiche (decision du fondateur du 04/11/2026, `docs/socle/fil-ariane-historique.md`).
 *
 * Le navigateur garde l'etat de chaque etape : l'origine survit donc au F5, a
 * Precedent et Suivant, et aux sauts entre apps ; chaque etape garde la sienne,
 * meme quand une meme fiche est ouverte deux fois depuis deux parents. Un lien
 * copie ou un nouvel onglet n'en ont pas : « je viens de quelque part » ne se
 * partage pas (regle du 09/09/2026, qui tient).
 */
export interface OrigineDEtape extends OrigineDeFiche {
  /** L'app de la fiche d'ou l'on vient. */
  app: string;
  /**
   * Le RANG de l'etape de la fiche d'ou l'on vient dans l'historique, ou `null`
   * quand rien n'a ete empile depuis elle (origine refaite depuis l'adresse,
   * ecran sans historique). C'est lui qui autorise a DEPILER : l'etape qu'on
   * occupe est juste apres la sienne.
   */
  rang: number | null;
  /** L'org ou l'origine a ete ecrite : une autre org ne la relit jamais. */
  org: string | null;
  /**
   * L'origine de la fiche d'ou l'on vient, rendue a son etape quand le retour
   * doit NAVIGUER au lieu de depiler. Profondeur bornee par le port.
   */
  parent: OrigineDEtape | null;
}

export interface ScreenUrlPort {
  /** Les parametres portes par l'adresse COURANTE. */
  params: ScreenSearch;
  /**
   * L'ORIGINE QUE PORTE L'ETAPE COURANTE (`OrigineDEtape`), deja verifiee par le
   * port (meme org), ou `null`. Facultatif : un port sans historique (un banc
   * d'essai) n'en a pas.
   */
  origine?: OrigineDEtape | null;
  /** Le rang de l'etape courante dans l'historique, ou `null` (pas d'historique). */
  rang?: number | null;
  /**
   * Ecrit les parametres. Par defaut on REMPLACE l'etape courante : changer de
   * vue, de section ou de pastille ne merite pas une etape d'historique (le
   * bouton Retour du navigateur deviendrait inutilisable a la troisieme
   * frappe). `push: true` cree une vraie etape : reserve a l'OUVERTURE d'une
   * fiche, la seule navigation dont le retour navigateur doit s'occuper.
   *
   * L'ORIGINE DE L'ETAPE : un REMPLACEMENT la GARDE (c'est la meme etape,
   * regardee autrement), un PUSH n'en pose aucune. `depuis` (push seulement)
   * dit que la nouvelle etape vient de la fiche courante : le port complete
   * l'app, le rang, l'org et le parent. `origine` pose une origine exacte sur
   * l'etape ecrite, ou l'efface (`null`).
   */
  setParams(
    next: ScreenSearch,
    options?: { push?: boolean; depuis?: OrigineDeFiche; origine?: OrigineDEtape | null },
  ): void;
  /**
   * Recule d'une etape. Sert a la SORTIE de fiche quand c'est nous qui avons
   * empile son etape : depiler proprement vaut mieux qu'empiler une etape de
   * plus, qui ferait rouvrir la fiche au premier Retour.
   */
  back(): void;
  /**
   * REVENIR A L'ORIGINE : `depiler` recule d'une etape ; `naviguer` REMPLACE
   * l'etape courante par l'adresse de l'origine, dans SON app, et rend a cette
   * etape sa propre origine (`parent`). Facultatif : sans lui, l'ecran recule,
   * ou rejoue l'adresse lui-meme quand l'origine est de son app.
   */
  revenir?(origine: OrigineDEtape, geste: 'depiler' | 'naviguer'): void;
}

/**
 * UNE ADRESSE NE PEUT DECRIRE QU'UN SEUL ETAT D'ECRAN, et c'est le point le
 * moins evident de ce fichier.
 *
 * Rien n'interdit a une app de monter DEUX `useScreen` sous la meme route :
 * l'app Maintenance le fait (les signalements et les interventions, deux
 * familles differentes, montees en permanence, une seule affichee). Sans
 * arbitre, les deux ecrivent les MEMES quatre cles, le dernier gagne, et le
 * perdant relit l'adresse pour s'appliquer l'etat de l'autre : la pastille de
 * l'un s'allume chez l'autre, et l'`id` d'un signalement part chercher une
 * intervention qui n'existe pas.
 *
 * D'ou un PROPRIETAIRE UNIQUE, attribue au premier qui demande (donc au premier
 * `useScreen` du fichier d'ecran). Les autres travaillent en memoire, comme
 * hors shell, et le disent en developpement. C'est volontairement automatique :
 * une app qui monte un second etat d'ecran ne doit pas avoir a connaitre ce
 * piege pour y echapper.
 */
interface ScreenUrlContextValue {
  port: ScreenUrlPort;
  /** Demande la propriete de l'adresse. Rend `true` si `key` la detient. */
  claim(key: string): boolean;
  /** Rend l'adresse (demontage de l'ecran proprietaire). */
  release(key: string): void;
}

const ScreenUrlContext = createContext<ScreenUrlContextValue | null>(null);

/** Branche les ecrans sur l'adresse. Fourni par le shell, sur la route d'une app. */
export function ScreenUrlProvider({
  value,
  children,
}: {
  value: ScreenUrlPort;
  children: ReactNode;
}): JSX.Element {
  const ownerRef = useRef<string | null>(null);
  // Stables : c'est ce qui evite que l'effet de propriete des ecrans se
  // rebranche a chaque navigation (le port, lui, change a chaque adresse).
  const claim = useCallback((key: string): boolean => {
    if (ownerRef.current == null) ownerRef.current = key;
    return ownerRef.current === key;
  }, []);
  const release = useCallback((key: string): void => {
    if (ownerRef.current === key) ownerRef.current = null;
  }, []);
  // L'objet de contexte est RECREE a chaque adresse (sinon les consommateurs ne
  // se re-rendraient pas), mais `claim` et `release` restent les memes.
  const contextValue: ScreenUrlContextValue = { port: value, claim, release };
  return <ScreenUrlContext.Provider value={contextValue}>{children}</ScreenUrlContext.Provider>;
}

/** Le port courant, ou `null` quand l'ecran tourne sans routeur. */
export function useScreenUrl(): ScreenUrlPort | null {
  return useContext(ScreenUrlContext)?.port ?? null;
}

/**
 * Le port RESERVE a un ecran : son port si l'adresse lui appartient, `null`
 * sinon (second etat d'ecran d'une meme route, ou pas de shell du tout). Dans
 * les deux cas `useScreen` se comporte exactement comme avant ce chantier.
 */
export function useScreenUrlOwnership(key: string): ScreenUrlPort | null {
  const context = useContext(ScreenUrlContext);
  const claim = context?.claim;
  const release = context?.release;
  // La propriete se demande des le PREMIER RENDU, pas dans un effet : c'est au
  // premier rendu que `useScreen` decide si son etat de depart vient de
  // l'adresse. Un initialiseur de `useState` ne tourne qu'une fois.
  const [owner, setOwner] = useState<boolean>(() => claim?.(key) ?? false);
  useEffect(() => {
    if (claim == null || release == null) return;
    // ON REDEMANDE, et on met a jour. Un refus au premier rendu ne vaut pas
    // pour toujours : React REND le nouvel ecran avant de nettoyer l'ancien,
    // donc un ecran qui arrive pendant qu'un autre part se voit d'abord
    // refuser l'adresse par un proprietaire qui n'existe deja plus. Sans cette
    // reprise, plus personne ne possedait l'adresse apres un changement
    // d'ecran : elle se figeait sur `/app/<id>` et ne bougeait plus jusqu'au
    // rechargement de la page. Les effets de nettoyage passent AVANT les
    // effets de montage dans le meme flush : quand celui-ci tourne, l'ancien
    // proprietaire a bien rendu sa place.
    const mine = claim(key);
    setOwner(mine);
    // L'avertissement vit ICI, et pas dans un effet sur `owner` : c'est le
    // seul endroit qui connaisse la reponse DEFINITIVE. Un refus au rendu peut
    // n'etre que le proprietaire sortant qui n'a pas encore ete nettoye, et
    // avertir la-dessus crierait au loup a chaque changement d'app.
    if (import.meta.env.DEV && !mine) {
      console.warn(
        `[taki/views] ${key} : un autre etat d'ecran possede deja l'adresse de cette ` +
          'route. Celui-ci vit donc en memoire : sa vue, sa fiche, sa section et sa ' +
          "pastille ne sont ni partageables, ni rejouees par F5. Une adresse ne peut " +
          "decrire qu'UN seul ecran ; si c'est celui-ci qui compte, monte-le en premier.",
      );
    }
    return () => release(key);
  }, [claim, release, key]);
  return owner ? (context?.port ?? null) : null;
}

/** Les seules cles que l'etat d'ecran possede dans l'adresse. */
export const SCREEN_SEARCH_KEYS = ['view', 'id', 's', 'f', 'arch', 'mod', 'dans', 'apercu'] as const;

/**
 * La valeur du parametre `arch` quand le mode Archives est allume. Une seule
 * valeur reconnue, et c'est voulu : tout le reste retombe sur "eteint".
 */
const ARCHIVES_ON = '1';

/**
 * La valeur du parametre `mod` quand le MODE MODELES est allume. Meme valeur
 * unique que `arch`, pour la meme raison : deux modes voisins qui liraient leur
 * parametre avec deux tolerances differentes finiraient par se comporter
 * differemment sur la meme adresse retouchee a la main.
 */
const MODELES_ON = '1';

/**
 * Ne garde que NOS cles, et jette les valeurs vides. Une app (ou le shell) peut
 * poser d'autres parametres sur la meme adresse : ils ne nous regardent pas, et
 * les ecraser serait un bug a distance.
 */
export function pickScreenSearch(params: ScreenSearch): ScreenSearch {
  const picked: ScreenSearch = {};
  for (const key of SCREEN_SEARCH_KEYS) {
    const value = params[key];
    if (typeof value === 'string' && value !== '') picked[key] = value;
  }
  return picked;
}

/**
 * Une empreinte STABLE d'un jeu de parametres : c'est elle qui repond a "cette
 * adresse dit-elle deja ce que l'etat dit ?". Sans elle, l'ecriture et la
 * relecture se repondraient en boucle.
 */
export function screenSearchKey(params: ScreenSearch): string {
  return SCREEN_SEARCH_KEYS.map((key) => `${key}=${params[key] ?? ''}`).join('&');
}

/**
 * Le parametre `f`, lu : une liste d'ids de filtres. Tolerant par construction
 * (valeur absente, virgules doublees, virgule finale, id REPETE d'une adresse
 * retouchee a la main) : ce qui sort est toujours une liste d'ids non vides et
 * SANS DOUBLON (une liste de filtres actifs n'en admet pas par contrat : un
 * doublon poserait deux tags identiques en 4a, et sa croix les retirerait tous
 * les deux d'un coup), a VERIFIER ensuite contre le catalogue de la porte
 * (c'est `useScreen` qui connait le catalogue, pas le transport).
 */
export function splitFilterIds(raw: string | undefined): string[] {
  if (raw == null || raw === '') return [];
  return [...new Set(raw.split(',').filter((id) => id !== ''))];
}

/**
 * Le parametre `f`, ecrit : `undefined` quand aucun filtre n'est actif (une
 * cle absente, jamais une cle vide : `pickScreenSearch` jette les valeurs
 * vides, autant ne pas les produire). La virgule est le separateur du contrat
 * d'adresse : un id qui en contient une se relirait en deux filtres inconnus,
 * d'ou l'avertissement de developpement, au moment ou l'id fautif se voit.
 */
export function joinFilterIds(ids: string[]): string | undefined {
  if (ids.length === 0) return undefined;
  if (import.meta.env.DEV) {
    const broken = ids.find((id) => id.includes(','));
    if (broken != null) {
      console.warn(
        `[taki/views] L'id de filtre "${broken}" contient une virgule : le parametre ` +
          "d'adresse `f` est une liste separee par des virgules, cet id se relira en " +
          'deux filtres inconnus. Renomme-le.',
      );
    }
  }
  return ids.join(',');
}

/**
 * Le parametre `arch`, lu : le MODE ARCHIVES est-il allume.
 *
 * TOUT CE QUI N'EST PAS EXACTEMENT `1` VAUT "ETEINT", y compris `arch=0`,
 * `arch=true`, `arch=oui` et une cle absente. C'est la meme regle de
 * tolerance que le `?s=` inconnu, et pour la meme raison : une adresse est du
 * texte venu de l'exterieur (un lien colle, un favori vieux de six mois, une
 * faute de frappe). Retomber sur "eteint" rend une liste ordinaire, que
 * l'utilisateur comprend ; l'alternative serait un ecran d'archives vide que
 * rien n'explique, ou pire, une liste vide qu'il prendrait pour une perte de
 * donnees.
 */
export function readArchivesParam(raw: string | undefined): boolean {
  return raw === ARCHIVES_ON;
}

/**
 * Le parametre `arch`, ecrit : `undefined` quand le mode est eteint (une cle
 * absente, jamais une cle vide ni un `arch=0`). L'etat de repos ne s'ecrit
 * pas : une adresse d'ecran ordinaire reste OCTET POUR OCTET celle d'avant ce
 * chantier, et aucun lien deja partage ne change de sens.
 */
export function writeArchivesParam(on: boolean): string | undefined {
  return on ? ARCHIVES_ON : undefined;
}

/**
 * Le parametre `mod`, lu : le MODE MODELES est-il allume.
 *
 * MEME REGLE QUE `arch` AU CARACTERE PRES : seul `1` allume, tout le reste
 * (`mod=0`, `mod=true`, `mod=oui`, cle absente) vaut "eteint". Une adresse est
 * du texte venu de l'exterieur, et retomber sur la liste ordinaire est le seul
 * repli qu'un utilisateur comprend.
 */
export function readModelesParam(raw: string | undefined): boolean {
  return raw === MODELES_ON;
}

/**
 * Le parametre `mod`, ecrit : `undefined` quand le mode est eteint (une cle
 * absente, jamais une cle vide ni un `mod=0`). L'etat de repos ne s'ecrit pas :
 * une adresse d'ecran ordinaire reste OCTET POUR OCTET celle d'avant ce
 * chantier, et aucun lien deja partage ne change de sens.
 */
export function writeModelesParam(on: boolean): string | undefined {
  return on ? MODELES_ON : undefined;
}

/**
 * LES DEUX MODES D'UNE ADRESSE, LUS ENSEMBLE, ET L'EXCLUSIVITE TENUE ICI.
 *
 * Archives et Modeles decident tous les deux, et SEULS, du jeu que l'ecran
 * montre : allumes en meme temps, ils diraient deux listes a la fois. L'etat
 * d'ecran les tient donc exclusifs (`setArchivesMode` eteint l'autre, et
 * reciproquement), et une adresse doit dire la meme chose que cet etat.
 *
 * `arch` GAGNE, ET CE N'EST PAS UN TIRAGE AU SORT : il existait AVANT. Les
 * adresses `arch=1` deja partagees (un mail, un ticket, un favori) doivent
 * ouvrir exactement la meme liste qu'au jour ou elles ont ete copiees, et un
 * `mod=1` ajoute a la main derriere ne peut pas leur faire changer de sens.
 *
 * UN SEUL ENDROIT LE TRANCHE, et c'est tout l'interet de cette fonction :
 * l'etat initial de `useScreen` et sa relecture d'adresse (`fromSearch`) posent
 * la meme question, et deux copies d'un meme arbitrage divergent au premier
 * ajustement.
 */
export function readModesParams(
  archRaw: string | undefined,
  modRaw: string | undefined,
): { archivesMode: boolean; modelesMode: boolean } {
  const archivesMode = readArchivesParam(archRaw);
  return { archivesMode, modelesMode: !archivesMode && readModelesParam(modRaw) };
}
